/**
 * GTM dataLayer event utilities
 * Imperative functions for pushing ecommerce events to the dataLayer.
 */

import type {
  AccountRecoEventParams,
  GtagEventName,
  GtagEventParams,
  GtagFlatEventParams,
  GtagItem,
} from "./types";
import type { AnalyticsIdentity } from "./AnalyticsIdentityContext";
import type { ProductPageData } from "@/types/product";
import type { ItemCard, ProductCardBlock } from "@/types/content";
import type { CartLine } from "@/types/cart";
import type { OrderConfirmationLine } from "@/api/checkout";

/**
 * Identity as it goes onto the dataLayer.
 *
 * `userId` is OMITTED for a guest rather than pushed as `null`. The GTM container's
 * "ID Detected" trigger — which gates `mixpanel.identify()` — fires whenever its
 * `userId` data-layer variable is anything other than the string `"undefined"`, so a
 * literal null would read as `"null"`, pass the check, and identify every anonymous
 * visitor as a user called "null". An absent key is what that trigger is written
 * against, and it is also what leaves GA4's User ID unset rather than set to a
 * string. `isCustomerB2b` is always sent — false is a real answer.
 *
 * `b2b_username` — a B2B member's Business Central customer number — rides along
 * on every event for B2B members only, and is likewise omitted rather than null.
 */
function identityFields(identity: AnalyticsIdentity) {
  return {
    ...(identity.userId !== null && { userId: identity.userId }),
    isCustomerB2b: identity.isCustomerB2b,
    ...(identity.b2bUsername && { b2b_username: identity.b2bUsername }),
  };
}

/**
 * Push an ecommerce event to the GTM dataLayer.
 *
 * The `{ ecommerce: null }` push is required: GA4 merges rather than replaces
 * the `ecommerce` object, so without it `items` from the previous event leak
 * into the next one (a product viewed earlier reappears inside `purchase`).
 *
 * Identity goes at EVENT level, next to `event`, not inside `ecommerce`. The
 * container's `DLV - userId` is a v2 data-layer variable named plainly `userId`, so
 * it can only ever see the top level; nesting it under `ecommerce` meant it resolved
 * only on the one event that pushed it flat (`login`). On the Stripe return page —
 * a fresh page load, where the once-per-session `login` guard stops that event
 * re-firing — nothing set it at all, `mixpanel.identify()` never ran, and `purchase`
 * landed under a `$device:` id with no user attached.
 *
 * A copy stays inside `ecommerce` for backwards compatibility: GTM variables may
 * already be reading `ecommerce.userId`, and dropping it would break them silently.
 * Remove the copy once the container is confirmed to read the event-level one.
 */
export function sendEvent(
  action: GtagEventName,
  params: GtagEventParams,
  identity: AnalyticsIdentity
) {
  if (window.dataLayer === undefined) return;
  const fields = identityFields(identity);
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: action,
    ...fields,
    ecommerce: { ...params, ...fields },
  });
}

/**
 * Push a non-ecommerce event (`login`, `sign_up`, the `account_reco_*` module
 * events) — no `ecommerce` object, so identity and params sit together at the
 * top level.
 */
export function sendFlatEvent(
  action: GtagEventName,
  params: GtagFlatEventParams | AccountRecoEventParams,
  identity: AnalyticsIdentity
) {
  if (window.dataLayer === undefined) return;
  window.dataLayer.push({
    event: action,
    ...params,
    ...identityFields(identity),
  });
}

/**
 * Maps cart lines to GA4 items. Shared by checkout, shipping and payment.
 *
 * `item_product_type` and `item_delivery_type` both come straight off the line: the
 * backend classifies each product once, so the two dimensions agree between the
 * funnel events and `purchase` without either side re-deriving them.
 */
export function cartLinesToItems(lines: CartLine[]): GtagItem[] {
  return lines.map((line, index) => ({
    index,
    item_id: line.sku ?? "",
    item_name: line.name,
    item_variant: line.variantReference ?? "",
    item_product_type: line.productType ?? "",
    item_delivery_type: line.deliveryType ?? "",
    price: line.unitPrice,
    quantity: line.quantity,
  }));
}

/**
 * Maps the lines of a finalised order to GA4 items — the `purchase` counterpart of
 * `cartLinesToItems`. Purchase reports the order the backend actually booked (prices,
 * quantities and discounts as finalised), not the cart the buyer submitted.
 */
export function confirmationLinesToItems(
  lines: OrderConfirmationLine[]
): GtagItem[] {
  return lines.map((line, index) => ({
    index,
    item_id: line.sku ?? "",
    item_name: line.name,
    item_variant: line.variantReference ?? "",
    item_product_type: line.productType ?? "",
    item_delivery_type: line.deliveryType ?? "",
    price: line.unitPriceWithTaxAmount,
    quantity: line.quantity,
  }));
}

/** Cart value excluding tax — the basis for pre-purchase funnel events. */
export function cartLinesValue(lines: CartLine[]): number {
  return lines.reduce((acc, line) => acc + line.totalPrice, 0);
}

/** Total VAT across cart lines. Zero for B2B / tax-exempt carts. */
export function cartLinesTax(lines: CartLine[]): number {
  return lines.reduce((acc, line) => acc + line.totalTax, 0);
}

/**
 * The three shapes a product reaches add-to-cart in: the full product page, a
 * backend ItemCard (suggested products), and a rendered ProductCardBlock
 * (listing grids and CMS-authored cards).
 */
export type TrackableProduct = ProductPageData | ItemCard | ProductCardBlock;

function isProductPage(product: TrackableProduct): product is ProductPageData {
  return "compositions" in product;
}

function isItemCard(product: TrackableProduct): product is ItemCard {
  return "header" in product;
}

/**
 * Track an add-to-cart event from any of the three product shapes.
 *
 * Items carry `item_product_type` only where the shape actually has it (the product
 * page); listing cards do not, and there is no `item_delivery_type` at all here —
 * that value is resolved server-side per order line, so it first exists once the
 * line does. Do not infer either of them from the product type on this side: the
 * rule lives in the backend's ProductDeliveryType and must stay there.
 */
export function trackAddToCart({
  product,
  currency,
  price,
  value,
  quantity = 1,
  variantSku,
  listName,
  identity,
}: {
  product: TrackableProduct;
  currency: string;
  price: number;
  value: number;
  quantity?: number;
  /** SKU of the chosen variant, for products sold in several versions. */
  variantSku?: string;
  /**
   * The list the product was added from, e.g. "my_account_devices". Stamped on
   * every item as GA4's `item_list_name` and `item_list_id`; omitted otherwise.
   */
  listName?: string;
  identity: AnalyticsIdentity;
}) {
  let productName: string;
  let items: GtagItem[];

  if (isProductPage(product)) {
    productName = product.name;

    if (product.type?.includes("bundle")) {
      // Bundle product — map included products
      items = (product.includedProducts ?? []).map((item, index) => ({
        index,
        item_category: "",
        item_id: item.compositions?.product?.sku ?? "",
        item_name: item.name,
        item_variant: "",
        item_product_type: item.compositions?.product?.productType ?? "",
        price: item.compositions?.product?.price?.calculatedPrice ?? 0,
      }));
    } else {
      items = [
        {
          currency,
          index: 0,
          item_category:
            product.compositions?.productContent?.mainCategory?.name ?? "",
          item_id: product.compositions?.product?.sku ?? variantSku ?? "",
          item_name: productName,
          item_variant: variantSku ?? "",
          item_product_type: product.compositions?.product?.productType ?? "",
          price,
          quantity,
        },
      ];
    }
  } else if (isItemCard(product)) {
    productName = product.header;
    items = [
      {
        currency,
        index: 0,
        item_category: product.productCategory ?? "",
        item_id: product.sku ?? variantSku ?? "",
        item_name: productName,
        item_variant: variantSku ?? "",
        price,
        quantity,
      },
    ];
  } else {
    // ProductCardBlock — a card rendered in a listing grid
    productName = product.productName;
    items = [
      {
        currency,
        index: 0,
        item_category: product.category ?? "",
        item_id: product.sku ?? variantSku ?? "",
        item_name: productName,
        item_variant: variantSku ?? "",
        price,
        quantity,
      },
    ];
  }

  if (listName) {
    items = items.map((item) => ({
      ...item,
      item_list_name: listName,
      item_list_id: listName,
    }));
  }

  sendEvent(
    "add_to_cart",
    {
      content_id: product.id,
      content_type: product.type,
      currency,
      items,
      screen_name: productName,
      value,
    },
    identity
  );
}

// ---------------------------------------------------------------------------
// Imperative funnel events
//
// These fire from event handlers rather than on mount, because they mark a
// user action (picking a delivery method, submitting payment, creating an
// account) rather than a page being seen.
// ---------------------------------------------------------------------------

/** GA4 `add_shipping_info` — fires when a delivery method is chosen. */
export function trackAddShippingInfo({
  lines,
  currency,
  shippingTier,
  deliveryType,
  discountCodes,
  identity,
}: {
  lines: CartLine[];
  currency: string;
  shippingTier: string;
  /** The cart's own `deliveryType`; omitted for an empty or older-backend cart. */
  deliveryType?: string | null;
  discountCodes?: string[];
  identity: AnalyticsIdentity;
}) {
  if (!lines?.length) return;
  sendEvent(
    "add_shipping_info",
    {
      coupon: discountCodes?.join(" - ") ?? "",
      currency,
      shipping_tier: shippingTier,
      delivery_type: deliveryType ?? undefined,
      items: cartLinesToItems(lines),
      value: cartLinesValue(lines),
    },
    identity
  );
}

/** GA4 `add_payment_info` — fires when payment details are submitted. */
export function trackAddPaymentInfo({
  lines,
  currency,
  paymentType,
  deliveryType,
  discountCodes,
  identity,
}: {
  lines: CartLine[];
  currency: string;
  paymentType: string;
  /** The cart's own `deliveryType`; omitted for an empty or older-backend cart. */
  deliveryType?: string | null;
  discountCodes?: string[];
  identity: AnalyticsIdentity;
}) {
  if (!lines?.length) return;
  sendEvent(
    "add_payment_info",
    {
      coupon: discountCodes?.join(" - ") ?? "",
      currency,
      payment_type: paymentType,
      delivery_type: deliveryType ?? undefined,
      items: cartLinesToItems(lines),
      value: cartLinesValue(lines),
    },
    identity
  );
}

/** GA4 `sign_up` — fires once when an account is created. */
export function trackSignUp({
  method,
  identity,
}: {
  method: string;
  identity: AnalyticsIdentity;
}) {
  sendFlatEvent("sign_up", { method }, identity);
}
